// Dr.Rx™ Regulatory Alert Ingestion & Resilient Fallback Service
import type { Alert } from '../types';
import { db } from './firebase';
import { doc, getDoc } from "firebase/firestore";
import mockNationalAlertsData from '../data/mockNationalAlerts.json';

const safeToDate = (value: any): string => {
    if (!value) return new Date().toISOString();
    if (value && typeof value.toDate === 'function') {
        return value.toDate().toISOString();
    }
    if (value && typeof value.seconds === 'number') {
        return new Date(value.seconds * 1000).toISOString();
    }
    if (typeof value === 'string' || typeof value === 'number') {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
            return date.toISOString();
        }
    }
    return new Date().toISOString();
};

const processAlerts = (alerts: any[]): Alert[] => {
    if (!Array.isArray(alerts)) return [];
    return alerts.map(a => ({
        ...a,
        date: safeToDate(a.date),
    }));
};

// --- NATIONAL ---

export const getNationalAlerts = async (sources: string[], isFallback: boolean): Promise<{ alerts: Alert[], lastUpdated: Date | null }> => {
    if (isFallback) {
        return getMockNationalAlerts(sources);
    }

    try {
        const docRef = doc(db, 'system_alerts', 'national');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            const allAlerts: Alert[] = processAlerts(data.alerts || []);
            const filtered = allAlerts.filter(a => sources.includes(a.source));
            
            if (filtered.length === 0) return getMockNationalAlerts(sources);

            const lastUpdatedDate = data.lastUpdated ? new Date(safeToDate(data.lastUpdated)) : null;

            return {
                alerts: filtered,
                lastUpdated: lastUpdatedDate
            };
        }
    } catch (error) {
        console.error("Error fetching national alerts from DB:", error);
    }

    return getMockNationalAlerts(sources);
};

export const getRawNationalAlerts = async (): Promise<{ alerts: Alert[], lastUpdated: Date | null }> => {
    try {
        const docRef = doc(db, 'system_alerts', 'national');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            return {
                alerts: processAlerts(data.alerts || []),
                lastUpdated: data.lastUpdated ? new Date(safeToDate(data.lastUpdated)) : null
            };
        }
        return { alerts: [], lastUpdated: null };
    } catch (error) {
        console.error("Admin fetch error:", error);
        throw error;
    }
};

export const getMockNationalAlerts = async (sources?: string[]) => {
  try {
    const processed = processAlerts(mockNationalAlertsData as any[]);
    const filtered = sources && sources.length > 0
      ? processed.filter(a => sources.includes(a.source))
      : processed;
    return { alerts: filtered, lastUpdated: new Date() };
  } catch (e) {
    return { alerts: [], lastUpdated: null };
  }
};

export const getNationalAlertsMetadata = async () => {
    try {
        const docRef = doc(db, 'system_alerts', 'national');
        const docSnap = await getDoc(docRef);
        return { lastUpdated: docSnap.exists() ? new Date(safeToDate(docSnap.data().lastUpdated)) : null };
    } catch {
        return { lastUpdated: null };
    }
};

// --- STATE ---
export const getStateAlerts = async (states: string[], _uid: string): Promise<Alert[]> => {
    if (states.length === 0) return [];

    try {
        const docRef = doc(db, 'system_alerts', 'states_batch_1');
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
            const allStateAlerts = processAlerts(docSnap.data().alerts as any[]);
            const filtered = allStateAlerts.filter(a => {
                const stateCode = a.state || (a.title && a.title.substring(0, 2));
                return states.some(s => stateCode?.includes(s));
            });
            if (filtered.length > 0) return filtered;
        }
    } catch (e) {
        console.error("Error fetching state alerts from DB", e);
    }
    return []; 
};
